import { hasPermission, hasAnyPermission } from '@/lib/permission-match';

/** 与 Sidebar 一致：未配置侧栏白名单时不限制；空数组表示无任何菜单 path */
export function menuPathAllowedByRoleConfig(
  path: string,
  menuKeys: string[] | undefined,
): boolean {
  if (menuKeys === undefined) return true;
  if (menuKeys.length === 0) return false;
  return menuKeys.includes(path);
}

/** 与 Sidebar / menu-manifest 一致；`true` 表示凡平台管理员即可（如「权限与菜单」） */
export const PLATFORM_PATH_PERMISSIONS: Record<
  string,
  string | string[] | true
> = {
  '/dashboard': 'platform:dashboard:view',
  '/order': 'platform:order:view',
  '/tenant': ['platform:tenant:view', 'platform:tenant:manage'],
  '/market-config': 'platform:market:mutate',
  '/plan-config': 'platform:market:mutate',
  '/platform-roles': 'platform:platform_user:manage',
  '/platform-users': 'platform:platform_user:manage',
  '/audit-logs': 'platform:audit:view',
  '/rbac': true,
};

/** 优先尝试的业务页，再落到说明类页 */
const PLATFORM_FALLBACK_ORDER: (keyof typeof PLATFORM_PATH_PERMISSIONS)[] = [
  '/dashboard',
  '/order',
  '/tenant',
  '/market-config',
  '/plan-config',
  '/platform-roles',
  '/platform-users',
  '/audit-logs',
  '/rbac',
];

export function platformRoutePermissionAllowed(
  pathname: string,
  perms: string[],
  dynamic?: Record<string, string | string[] | true> | null,
): boolean {
  const merged: Record<string, string | string[] | true> = {
    ...PLATFORM_PATH_PERMISSIONS,
    ...(dynamic ?? {}),
  };
  const req = merged[pathname];
  if (req === undefined) return false;
  if (req === true) return true;
  if (Array.isArray(req)) return hasAnyPermission(perms, req);
  return hasPermission(perms, req);
}

/** 权限 ∩ 侧栏 menuKeys（与 Phase 8 Sidebar 求交一致） */
export function canAccessPlatformRoute(
  pathname: string,
  perms: string[],
  menuKeys?: string[],
  dynamic?: Record<string, string | string[] | true> | null,
): boolean {
  if (!platformRoutePermissionAllowed(pathname, perms, dynamic)) return false;
  return menuPathAllowedByRoleConfig(pathname, menuKeys);
}

export function getFirstAccessiblePlatformPath(
  perms: string[],
  menuKeys?: string[],
  dynamic?: Record<string, string | string[] | true> | null,
): string | null {
  for (const p of PLATFORM_FALLBACK_ORDER) {
    if (canAccessPlatformRoute(p, perms, menuKeys, dynamic)) return p;
  }
  return null;
}

/** 仅按权限码（忽略 menuKeys），用于 403 页「返回」兜底 */
export function getFirstAccessiblePlatformPathIgnoringMenu(
  perms: string[],
  dynamic?: Record<string, string | string[] | true> | null,
): string | null {
  for (const p of PLATFORM_FALLBACK_ORDER) {
    if (platformRoutePermissionAllowed(p, perms, dynamic)) return p;
  }
  return null;
}

export const TENANT_PATH_PERMISSIONS: Record<
  string,
  string | string[] | true
> = {
  '/workspace': 'tenant:workspace:view',
  '/market': 'tenant:market:view',
  '/team': ['tenant:user:view', 'tenant:user:manage'],
  '/team/roles': ['tenant:user:view', 'tenant:user:manage'],
  '/team/audit-logs': ['tenant:user:view', 'tenant:user:manage'],
};

const TENANT_FALLBACK_ORDER: (keyof typeof TENANT_PATH_PERMISSIONS)[] = [
  '/workspace',
  '/market',
  '/team',
  '/team/roles',
  '/team/audit-logs',
];

export function tenantRoutePermissionAllowed(
  pathname: string,
  perms: string[],
): boolean {
  const req = TENANT_PATH_PERMISSIONS[pathname];
  if (req === undefined) return false;
  if (req === true) return true;
  if (Array.isArray(req)) return hasAnyPermission(perms, req);
  return hasPermission(perms, req);
}

export function canAccessTenantRoute(
  pathname: string,
  perms: string[],
  menuKeys?: string[],
): boolean {
  if (!tenantRoutePermissionAllowed(pathname, perms)) return false;
  return menuPathAllowedByRoleConfig(pathname, menuKeys);
}

export function getFirstAccessibleTenantPath(
  perms: string[],
  menuKeys?: string[],
): string | null {
  for (const p of TENANT_FALLBACK_ORDER) {
    if (canAccessTenantRoute(p, perms, menuKeys)) return p;
  }
  return null;
}

export function getFirstAccessibleTenantPathIgnoringMenu(
  perms: string[],
): string | null {
  for (const p of TENANT_FALLBACK_ORDER) {
    if (tenantRoutePermissionAllowed(p, perms)) return p;
  }
  return null;
}
